import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { EditShipFromContactModal } from 'ui-components/components/edit-shipfrom-contact-modal';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { NewShipFromContactModal } from 'ui-components/components/new-shipfrom-contact-modal';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './manage-ship-from-contact-modal.module.less';
import { messages } from './messages';
import { deleteShipFromContact, extractErrorCollectionToMessageList } from '../../../carrier-pickup/carrier-pickup-api';
import { ManageItemGrid } from '../../../components/manage-item-grid';
import { ModalPortal } from '../../../components/modal-portal';
import { useModal } from '../../../hooks/use-modal';
import { PickupContactPropType } from '../../../prop-types/pickup-contact';

export const ManageShipFromContactModal = ({
  contacts,
  onClose,
  shipFromId = 0,
  showDefaultLocationNotes = false,
  onCreate,
  onEdit,
  onRemove,
}) => {
  const [errorCollection, setErrorCollection] = useAsyncSafeState([]);
  const { isOpen: isNewShipFromContactOpen, open: openNewShipFromContact, close: closeNewShipFromContact } = useModal();
  const { isOpen: isEditShipFromContactOpen, open: openEditShipFromContact, close: closeEditShipFromContact } = useModal();
  const [selectedContact, setSelectedContact] = useState({});
  const { formatMessage } = useIntl();
  const [isRemoving, setIsRemoving] = useState(false);

  const manageShipfromItemGridColumns = [
    {
      name: formatMessage(messages.manageShipFromContactNameColumn),
      itemRenderer: (contact) => `${contact.firstName} ${contact.lastName}`,
    },
  ];

  const onDeleteShipfromContact = useCallback(
    (contact) => {
      setIsRemoving(true);
      deleteShipFromContact(shipFromId, contact.shipFromContactId)
        .then(() => onRemove(contact.shipFromContactId))
        .catch(({ response }) => setErrorCollection(extractErrorCollectionToMessageList(response, [])))
        .finally(() => setIsRemoving(false));
    },
    [setErrorCollection, shipFromId, onRemove],
  );

  const handleEditShipFromContact = useCallback(
    (contact) => {
      setSelectedContact(contact);
      openEditShipFromContact();
    },
    [openEditShipFromContact],
  );

  const onCreateShipFromContact = useCallback(
    (contact) => {
      onCreate(contact);
      closeNewShipFromContact();
    },
    [closeNewShipFromContact, onCreate],
  );

  const onEditShipFromContact = useCallback(
    (shipFromContactId, contact) => {
      onEdit(shipFromContactId, contact);
      closeEditShipFromContact();
    },
    [closeEditShipFromContact, onEdit],
  );

  return (
    <>
      <ModalPortal onClose={onClose}>
        <Modal data-testid='manage-ship-from-contact-modal' size='small'>
          <ModalHeader>
            <FormattedMessage {...messages.manageShipFromContactHeader} />
          </ModalHeader>
          <ModalBody className={style['modal-body']}>
            {isRemoving ? (
              <div className={style['loading-indicator-container']} data-testid='manage-shipfrom-loading'>
                <LoadingIndicator className={style['loading-indicator']} />
              </div>
            ) : (
              <>
                {errorCollection.map((message, index) => (
                  <BannerMessageV2 key={index} content={message} status={BannerStatus.Error} />
                ))}
                <ManageItemGrid
                  columns={manageShipfromItemGridColumns}
                  items={contacts}
                  onItemDeleted={onDeleteShipfromContact}
                  onItemEdited={handleEditShipFromContact}
                />
              </>
            )}
          </ModalBody>
          <ModalFooter className={style['modal-footer']}>
            <Button onClick={openNewShipFromContact} theme='link'>
              <FormattedMessage {...messages.createTitle} />
            </Button>
            <Button onClick={onClose} theme='primary'>
              <FormattedMessage {...messages.manageShipFromContactDoneButton} />
            </Button>
          </ModalFooter>
        </Modal>
      </ModalPortal>
      {isNewShipFromContactOpen ? (
        <NewShipFromContactModal
          onCancel={closeNewShipFromContact}
          onClose={closeNewShipFromContact}
          onCreate={onCreateShipFromContact}
          shipFromId={shipFromId}
          showDefaultLocationNotes={showDefaultLocationNotes}
        />
      ) : null}
      {isEditShipFromContactOpen && selectedContact ? (
        <EditShipFromContactModal
          contact={selectedContact}
          onCancel={closeEditShipFromContact}
          onClose={closeEditShipFromContact}
          onEdit={onEditShipFromContact}
          shipFromId={shipFromId}
          showDefaultLocationNotes={showDefaultLocationNotes}
        />
      ) : null}
    </>
  );
};

ManageShipFromContactModal.propTypes = {
  contacts: PropTypes.arrayOf(PickupContactPropType).isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  shipFromId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showDefaultLocationNotes: PropTypes.bool,
};
